/* Hook for searching books via the OpenLibrary API. Returns results with title, author, cover, year, and page count. */

import { useState, useCallback } from "react";

const OPEN_LIBRARY_SEARCH_URL = "https://openlibrary.org/search.json";
const OPEN_LIBRARY_COVER_URL = "https://covers.openlibrary.org/b/id";
const SEARCH_LIMIT = 8;

interface OpenLibraryDoc {
  key: string;
  title: string;
  author_name?: string[];
  first_publish_year?: number;
  cover_i?: number;
  number_of_pages_median?: number;
  edition_key?: string[];
}

export interface BookSearchResult {
  key: string;
  title: string;
  author: string;
  year?: number;
  coverUrl?: string;
  pageCount?: number;
  link: string;
}

async function fetchEditionPageCount(editionKeys: string[]): Promise<number | undefined> {
  for (const editionKey of editionKeys.slice(0, 3)) {
    try {
      const res = await fetch(`https://openlibrary.org/books/${editionKey}.json`);
      if (!res.ok) continue;
      const data = await res.json();
      if (data.number_of_pages && data.number_of_pages > 0) {
        return data.number_of_pages;
      }
    } catch {
      continue;
    }
  }
  return undefined;
}

export function useBookSearch() {
  const [results, setResults] = useState<BookSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const url = `${OPEN_LIBRARY_SEARCH_URL}?q=${encodeURIComponent(trimmed)}&limit=${SEARCH_LIMIT}`;
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error("Failed to search OpenLibrary");
      }
      const data = await res.json();
      const docs: OpenLibraryDoc[] = data.docs ?? [];

      const mapped: BookSearchResult[] = await Promise.all(
        docs.map(async (doc) => {
          let pageCount = doc.number_of_pages_median;
          if (!pageCount && doc.edition_key) {
            pageCount = await fetchEditionPageCount(doc.edition_key);
          }

          return {
            key: doc.key,
            title: doc.title,
            author: doc.author_name?.[0] ?? "",
            year: doc.first_publish_year,
            coverUrl: doc.cover_i
              ? `${OPEN_LIBRARY_COVER_URL}/${doc.cover_i}-M.jpg`
              : undefined,
            pageCount,
            link: `https://openlibrary.org${doc.key}`,
          };
        }),
      );

      setResults(mapped);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Search failed";
      setError(message);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  return { results, isSearching, error, search };
}
